import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { parseError } from '../shared/error';
import { v1IncidentKeys } from './v1Incident.queryKeys';
import { UpdateV1IncidentBody, V1Incident } from './v1Incident.types';
import { applyExerciseIncidentName } from 'utils/incidentExerciseName';
import {
  invalidateIncidentQueries,
  keys as incidentKeys,
} from 'components/IAP/hooks/incidentHooks';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { updateIncidentDetailsSuccess } from 'actions/diceActions';

export function useUpdateV1IncidentMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const groupGuid = useGroupGuid();

  return useMutation<
    V1Incident,
    undefined,
    { v1IncidentId: UUID } & UpdateV1IncidentBody
  >({
    mutationFn: async ({ v1IncidentId, ...patch }) => {
      const name = applyExerciseIncidentName(patch.name, patch.incidentTypes);
      const resp = await axios.patch<V1Incident>(
        `${config.apiGateway.v1Incidents}/${v1IncidentId}`,
        {
          ...patch,
          name,
        }
      );

      return resp.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: v1IncidentKeys.get(data.id) });
      queryClient.invalidateQueries({ queryKey: incidentKeys.get(data.id) });
      invalidateIncidentQueries(queryClient, groupGuid);
      // Redux holds a login-time snapshot of incidents; patch the updated
      // incident so surfaces reading from the store (e.g. the AI chat
      // workspace label) update without a full page reload. The store uses
      // snake_case (IncidentAugmented), so map from the camelCase response.
      dispatch(
        updateIncidentDetailsSuccess({
          incidentId: data.id,
          changes: {
            name: data.name,
            archived: data.archived,
            settings: data.settings,
            incident_types: data.incidentTypes,
          },
        })
      );
      toast.success('Incident updated.');
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
}
